import React, { useState, useEffect, useRef } from 'react';
import '../estilos.css';
import config from '../config.json';
import LanguageSelection from './LanguageSelection';
import { generarUUID } from '../Component/generadorUUID';
import TablesCreated from '../Component/TablesCreated';

const KEYWORDS = [
  'SELECT','INSERT','UPDATE','DELETE','CREATE','DROP','ALTER','TABLE','DATABASE',
  'FROM','WHERE','JOIN','LEFT','RIGHT','INNER','OUTER','ON','AS','AND','OR','NOT',
  'IN','IS','NULL','VALUES','INTO','SET','IF','EXISTS','PRIMARY','KEY','FOREIGN',
  'REFERENCES','AUTO_INCREMENT','DEFAULT','UNIQUE','INDEX','CONSTRAINT','ENGINE',
  'CHARSET','COLLATE','COMMIT','ROLLBACK','TRANSACTION','START','BEGIN','END',
  'MODIFY','ADD','COLUMN','INT','VARCHAR','TEXT','DATE','DATETIME','FLOAT',
  'DOUBLE','DECIMAL','TINYINT','BIGINT','BOOLEAN','BOOL','NOT NULL','UNSIGNED',
  'SHOW','TABLES','DESCRIBE','USE','LIMIT','ORDER','BY','GROUP','HAVING',
  'DISTINCT','COUNT','SUM','AVG','MAX','MIN','LIKE','BETWEEN','CASE','WHEN',
  'THEN','ELSE','TRUNCATE','REPLACE','GRANT','REVOKE','IF NOT EXISTS',
];

function highlightSQL(sql: string): string {
  let result = sql
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Comentarios
  result = result.replace(/(--[^\n]*)/g, '<span style="color:#6A9955;font-style:italic">$1</span>');
  result = result.replace(/(\/\*[\s\S]*?\*\/)/g, '<span style="color:#6A9955;font-style:italic">$1</span>');

  // Strings
  result = result.replace(/('(?:[^'\\]|\\.)*')/g, '<span style="color:#CE9178">$1</span>');

  // Backticks
  result = result.replace(/(`[^`]*`)/g, '<span style="color:#9CDCFE">$1</span>');

  // Números
  result = result.replace(/\b(\d+)\b/g, '<span style="color:#B5CEA8">$1</span>');

  // Keywords
  const keywordRegex = new RegExp(`\\b(${KEYWORDS.join('|')})\\b`, 'gi');
  result = result.replace(keywordRegex, '<span style="color:#569CD6;font-weight:600">$1</span>');

  return result;
}

const LINE_HEIGHT = 21; // px — 14px font * 1.5 lineHeight

const Consola: React.FC = () => {
  const [sqlCommand, setSqlCommand] = useState('');
  const [sessionUUID, setSessionUUID] = useState<string | null>(null);
  const [showModals, setShowModals] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState<any[]>([]);
  const [refreshTables, setRefreshTables] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const uuid = localStorage.getItem('sessionUUID');
    setSessionUUID(uuid);
  }, []);

  const lineCount = sqlCommand.split('\n').length;

  const handleScroll = () => {
    if (!textareaRef.current) return;
    const st = textareaRef.current.scrollTop;
    const sl = textareaRef.current.scrollLeft;
    setScrollTop(st);
    if (highlightRef.current) {
      highlightRef.current.scrollTop = st;
      highlightRef.current.scrollLeft = sl;
    }
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = st;
    }
  };

  const processSQL = (sql: string) => {
    const lines = sql.split('\n').filter(line => {
      const trimmed = line.trim();
      return (
        trimmed !== '' &&
        !trimmed.startsWith('--') &&
        !trimmed.startsWith('/*!') &&
        !trimmed.startsWith('SET ') &&
        !trimmed.startsWith('START ') &&
        !trimmed.startsWith('COMMIT') &&
        !trimmed.startsWith('ALTER ') &&
        !trimmed.startsWith('MODIFY ')
      );
    });

    const cleanedSQL = lines.join('\n');
    const cleanedSQLs: string[] = [];

    const createTableRegex = /CREATE TABLE\s+`?([a-zA-Z0-9_]+)`?\s*\(([\s\S]+?)\)\s*ENGINE[^;]*;/gi;
    let match;
    while ((match = createTableRegex.exec(cleanedSQL)) !== null) {
      const tableName = match[1];
      const columnsPart = match[2];
      const columnLines = columnsPart.split(',\n').map(c => c.trim()).filter(Boolean);
      const processedColumns: string[] = [];
      columnLines.forEach(col => {
        if (/^(KEY|UNIQUE KEY|PRIMARY KEY|CONSTRAINT)/i.test(col)) return;
        col = col.replace(/`/g, '');
        processedColumns.push(col);
      });
      cleanedSQLs.push(`CREATE TABLE IF NOT EXISTS \`${tableName}\` (${processedColumns.join(', ')});`);
    }

    const insertRegex = /INSERT INTO\s+`?[a-zA-Z0-9_]+`?\s*\([^)]+\)\s*VALUES[\s\S]+?;/gi;
    while ((match = insertRegex.exec(cleanedSQL)) !== null) {
      cleanedSQLs.push(match[0].replace(/`/g, ''));
    }

    return cleanedSQLs.length > 0 ? cleanedSQLs : null;
  };

  const handleExecute = async () => {
    if (!sqlCommand) {
      alert('Por favor, ingresa una sentencia SQL válida.');
      return;
    }
    const cleanedSQLs = processSQL(sqlCommand.trim());
    if (!cleanedSQLs) return;
    setIsLoading(true);
    try {
      for (const cleanedSQL of cleanedSQLs) {
        const response = await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ databaseName: sessionUUID, sqlQuery: cleanedSQL }),
        });
        const result = await response.json();
        if (!response.ok) { alert(`Error: ${result.message}`); break; }
      }
      setRefreshTables(prev => !prev);
      alert('Sentencias SQL ejecutadas con éxito.');
    } catch (error) {
      alert('Hubo un error desconocido al ejecutar la sentencia.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    setShowModals(false);
    setTimeout(() => setShowModals(true), 10);
  };

  const monoStyle: React.CSSProperties = {
    fontFamily: '"Consolas", "Courier New", monospace',
    fontSize: '14px',
    lineHeight: `${LINE_HEIGHT}px`,
    whiteSpace: 'pre',
    overflowWrap: 'normal',
  };

  const ROWS = 23;
  const editorHeight = ROWS * LINE_HEIGHT;

  return (
    <div>
      <style>{`
        .sql-editor-wrapper {
          display: flex;
          border: 1px solid #d0d0d0;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          background: #fff;
        }
        .sql-line-numbers {
          background: #f5f5f5;
          border-right: 1px solid #e0e0e0;
          padding: 10px 0;
          min-width: 48px;
          text-align: right;
          overflow: hidden;
          flex-shrink: 0;
          user-select: none;
        }
        .sql-line-numbers span {
          display: block;
          padding: 0 12px 0 8px;
          color: #aaa;
          font-size: 13px;
          font-family: "Consolas", monospace;
          line-height: ${LINE_HEIGHT}px;
        }
        .sql-line-numbers span.active-line {
          color: #555;
          font-weight: 600;
        }
        .sql-editor-body {
          position: relative;
          flex: 1;
          overflow: hidden;
        }
        .sql-highlight-layer {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          padding: 10px 14px;
          box-sizing: border-box;
          pointer-events: none;
          overflow: hidden;
          color: #333;
          background: #fff;
        }
        .sql-textarea {
          position: relative;
          display: block;
          width: 100%;
          height: 100%;
          padding: 10px 14px;
          box-sizing: border-box;
          background: transparent;
          color: transparent;
          caret-color: #333;
          border: none;
          outline: none;
          resize: none;
          overflow: auto;
          z-index: 1;
        }
        .sql-textarea::placeholder {
          color: #bbb;
        }
        .sql-toolbar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f5f5f5;
          border: 1px solid #d0d0d0;
          border-top: none;
          border-radius: 0 0 6px 6px;
        }
      `}</style>

      <div>
        {isLoading && <div className="spinner">Cargando...</div>}
        <LanguageSelection
          sessionUUID={sessionUUID}
          show={showModals}
          onClose={() => setShowModals(false)}
          tableName={tableName}
          columns={columns}
          onGenerate={(framework) => console.log("Framework:", framework)}
        />

        {/* Barra superior tipo IDE */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 14px',
          background: '#f0f0f0',
          border: '1px solid #d0d0d0',
          borderBottom: 'none',
          borderRadius: '6px 6px 0 0',
          fontSize: '13px', color: '#555',
          fontFamily: 'Consolas, monospace',
        }}>
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: '#FF5F57', display: 'inline-block',
          }} />
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: '#FFBD2E', display: 'inline-block',
          }} />
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: '#28CA41', display: 'inline-block',
          }} />
          <span style={{ marginLeft: '8px', color: '#777' }}>SQL Editor</span>
        </div>

        {/* Editor */}
        <div className="sql-editor-wrapper" style={{ height: `${editorHeight + 20}px` }}>

          {/* Números de línea */}
          <div className="sql-line-numbers" ref={lineNumbersRef}>
            {Array.from({ length: Math.max(lineCount, ROWS) }, (_, i) => (
              <span key={i}>{i + 1}</span>
            ))}
          </div>

          {/* Área de código */}
          <div className="sql-editor-body">
            <div
              ref={highlightRef}
              className="sql-highlight-layer"
              style={monoStyle}
              dangerouslySetInnerHTML={{ __html: highlightSQL(sqlCommand) + '\n' }}
            />
            <textarea
              ref={textareaRef}
              className="sql-textarea"
              style={monoStyle}
              rows={ROWS}
              value={sqlCommand}
              onChange={e => setSqlCommand(e.target.value)}
              onScroll={handleScroll}
              placeholder="-- Escribe tus sentencias SQL aquí..."
              spellCheck={false}
            />
          </div>
        </div>

        {/* Toolbar inferior */}
        <div className="sql-toolbar">
          <button onClick={handleExecute} style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '4px 12px', fontSize: '12px', fontWeight: 500,
            background: '#1a7fd4', color: '#fff',
            border: 'none', borderRadius: '4px', cursor: 'pointer',
            fontFamily: 'Consolas, monospace',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = '#1569b5')}
            onMouseLeave={e => (e.currentTarget.style.background = '#1a7fd4')}
          >
            <span style={{ fontSize: '10px' }}>▶</span> Run
          </button>
          <button onClick={handleNext} style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '4px 12px', fontSize: '12px', fontWeight: 500,
            background: 'transparent', color: '#555',
            border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer',
            fontFamily: 'Consolas, monospace',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f0f0f0')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            Generar Proyecto
          </button>
          <span style={{ marginLeft: 'auto', color: '#999', fontSize: '12px', fontFamily: 'Consolas, monospace' }}>
            {lineCount} {lineCount === 1 ? 'línea' : 'líneas'}
          </span>
        </div>
      </div>

      <div>
        <TablesCreated sessionUUID={sessionUUID} refresh={refreshTables} />
      </div>
    </div>
  );
};

export default Consola;